// b12: motorerna, resorna och förbrukningen.
//
// På Hälsa: kortet Motorerna (värdena per motor, och "finns inte" för det som motorn inte
// sänder), kortet Kurs och autopilot, och kortet Resor med de senaste resorna, den ekonomiska
// farten, räckvidden och servicen.
//
// Sidan Resor (#plats/<id>/resor): resorna, förbrukningskurvan för säsongen och de senaste 30
// dagarna, räckvidden, provkörningarna och servicen. Ägaren och läsarna ser den. Ägaren startar
// och stoppar en provkörning och kvitterar servicen. Sidan för en resa (#plats/<id>/resor/<id>)
// har minuterna och resans Info. Båtkoll ändrar aldrig något i motorerna, autopiloten eller
// Humphree.
//
// b13: räckvidden per sida ("Babord 142 sjömil, styrbord 128 sjömil. Båten: 128 sjömil"),
// Volvos egna värden bredvid navets (PGN 127496 och 127497), kurvan per motor, dieseltankarna
// före och efter en resa, och tankningen som ägaren skriver in.

import {
  datumOchTid,
  type Fardkort,
  type Fardrad,
  type Fardsida,
  type Fardvisning,
  type Forbrukningskurva,
  type Halsa,
  KURVA,
  MOTORER,
  MOTORPLATSNAMN,
  type Motor,
  motorerI,
  motorstatustext,
  type PerMotor,
  type Provkorning,
  type Rackvidd,
  type Servicelage,
  sedan,
  tal,
} from "@batkoll/gemensam";
import { useCallback, useEffect, useState } from "preact/hooks";
import { api, Felsvar } from "../api.ts";
import { Kort, Rad } from "../komponenter.tsx";
import { arGammalt } from "../visa.ts";

/** b14: motorernas platser: Babord, Styrbord, Mitten, Generatorn och Utombordaren. */
const MOTORNAMN: Record<Motor, string> = MOTORPLATSNAMN;

/** "Babord motor" och "Styrbord motor" som i b12, annars platsens namn: "Generatorn". */
const motorrubrik = (m: Motor) =>
  m === "babord" || m === "styrbord" ? `${MOTORNAMN[m]} motor` : MOTORNAMN[m];

/** "babords tank" ur tankens namn. */
const forstaLiten = (t: string) => t.charAt(0).toLowerCase() + t.slice(1);

export const AUTOPILOT: Record<string, string> = {
  standby: "Standby",
  manuell: "Manuell",
  auto: "Auto",
  vind: "Vind",
  spar: "Spår",
  ingen_drift: "Ingen drift",
  okant: "Okänt",
  blandat: "Blandat",
};

const FINNS_INTE = "finns inte";

function tal2(v: unknown, enhet: string, decimaler = 1): string {
  return typeof v === "number" ? `${tal(v, decimaler)} ${enhet}` : FINNS_INTE;
}

/** Resans tid, till exempel "1 h 31 min" eller "45 min". */
export function langd(sekunder: number): string {
  const min = Math.round(sekunder / 60);
  return min < 60 ? `${min} min` : `${Math.floor(min / 60)} h ${min % 60} min`;
}

/** b12: motorerna på Hälsa, per motor. Det som motorn inte sänder står som "finns inte". */
export function Motorkort(props: { h: Halsa; nu: number }) {
  const { h, nu } = props;
  // b14: motorerna som hörs. Babord och styrbord visas tillsammans, som i b12.
  const hors = (m: Motor) =>
    Object.keys(h.signaler).some((k) => k.startsWith("motor.") && k.endsWith(`@${m}`));
  const sidor = hors("babord") || hors("styrbord");
  const visade = MOTORER.filter((m) => hors(m) || (sidor && (m === "babord" || m === "styrbord")));
  const har = visade.some(
    (m) => h.signaler[`motor.varvtal@${m}`] || h.signaler[`motor.timmar@${m}`],
  );
  if (!har) return null;
  const s = (namn: string, m: Motor) => h.signaler[`${namn}@${m}`];
  const service = h.farder?.service ?? [];
  const varning = visade.some((m) => {
    const st = s("motor.status", m)?.varde;
    return typeof st === "string" && st !== "";
  });
  const rader: [string, string, string, number][] = [
    ["Varvtal", "motor.varvtal", "rpm", 0],
    ["Bränsleflöde", "motor.bransle", "l/h", 1],
    ["Kylvatten", "motor.kylvatten_temp", "°C", 0],
    ["Oljetryck", "motor.oljetryck", "kPa", 0],
    ["Generatorn", "motor.laddning", "V", 1],
    ["Last", "motor.last", "%", 0],
    ["Timräknaren", "motor.timmar", "h", 1],
  ];
  return (
    <Kort rubrik="Motorerna" ton={varning ? "varning" : undefined} bred>
      <div class="motorer">
        {visade.map((m) => {
          const status = s("motor.status", m)?.varde;
          const lage = service.find((x) => x.motor === m);
          return (
            <section key={m} class="motor" aria-label={motorrubrik(m)}>
              <h3>{MOTORNAMN[m]}</h3>
              {rader.map(([namn, signal, enhet, d]) => (
                <Rad key={signal} namn={namn} gammalt={arGammalt(s(signal, m), nu)}>
                  {tal2(s(signal, m)?.varde, enhet, d)}
                </Rad>
              ))}
              <Rad namn="Motorn säger">
                {typeof status === "string"
                  ? status === ""
                    ? "inga varningar"
                    : status.split(", ").map(motorstatustext).join(", ")
                  : FINNS_INTE}
              </Rad>
              {lage && lage.intervall !== null && <Rad namn="Service">{lage.text}</Rad>}
            </section>
          );
        })}
      </div>
    </Kort>
  );
}

/** b12: kursen, trimmen, slagsidan och autopiloten på Hälsa. */
export function Kurskort(props: { h: Halsa; nu: number }) {
  const { h, nu } = props;
  const v = (k: string) => h.signaler[k]?.varde;
  const g = (k: string) => arGammalt(h.signaler[k], nu);
  const har = ["bat.kurs", "bat.trim", "bat.slagsida", "autopilot.lage"].some((k) => h.signaler[k]);
  if (!har) return null;
  const lage = v("autopilot.lage");
  return (
    <Kort rubrik="Kurs och autopilot">
      <Rad namn="Kursen" gammalt={g("bat.kurs")}>
        {tal2(v("bat.kurs"), "°", 0)}
      </Rad>
      <Rad namn="Girhastigheten" gammalt={g("bat.girhastighet")}>
        {tal2(v("bat.girhastighet"), "°/s", 1)}
      </Rad>
      <Rad namn="Trimmen" gammalt={g("bat.trim")}>
        {tal2(v("bat.trim"), "°", 1)}
      </Rad>
      <Rad namn="Slagsidan" gammalt={g("bat.slagsida")}>
        {tal2(v("bat.slagsida"), "°", 1)}
      </Rad>
      <Rad namn="Autopiloten" gammalt={g("autopilot.lage")}>
        {typeof lage === "string" ? (AUTOPILOT[lage] ?? lage) : FINNS_INTE}
      </Rad>
      <Rad namn="Styrkursen" gammalt={g("autopilot.kurs")}>
        {tal2(v("autopilot.kurs"), "°", 0)}
      </Rad>
      <p class="liten">Båtkoll läser bara. Inget ändras i autopiloten eller Humphree.</p>
    </Kort>
  );
}

function Resrad(props: { r: Fardrad; platsId: string; nu: number }) {
  const r = props.r;
  return (
    <li>
      <a href={`#plats/${props.platsId}/resor/${r.id}`}>
        {datumOchTid(r.start)}
        {r.pagar ? " (pågår)" : ""}
      </a>
      <span class="liten">
        {" "}
        {tal(r.distansNm, 1)} sjömil, {langd(r.sekunder)}
        {r.literTotalt !== null ? `, ${tal(r.literTotalt, 0)} liter` : ""}
        {r.literPerNm !== null ? `, ${tal(r.literPerNm, 2)} l/NM` : ""}
      </span>
    </li>
  );
}

/** b13: hur litrarna räknades. */
const hur = (h: "tabell" | "linjar" | null | undefined) =>
  h === "tabell" ? ", med tankens tabell" : h === "linjar" ? ", linjärt" : "";

function Rackviddsrader(props: { r: Rackvidd; nu: number }) {
  const r = props.r;
  const grupper = r.grupper ?? [];
  const v = r.volvo;
  const kvar = (t: string) => (t.startsWith("bensin") ? "Bensin kvar" : "Diesel kvar");
  return (
    <>
      {grupper.length === 0 && r.literKvar !== null && (
        <Rad namn={r.dieselProcent === null ? "Bränsle kvar" : "Diesel kvar"}>
          {tal(r.literKvar, 0)} liter
          {r.dieselProcent !== null && (
            <>
              {" "}
              ({tal(r.dieselProcent, 0)} %{hur(r.hur)}
              {r.dieselTid !== null ? `, senast kända ${sedan(r.dieselTid, props.nu)}` : ""})
            </>
          )}
        </Rad>
      )}
      {grupper.length === 0 && r.nu && (
        <Rad namn={`Med farten nu, ${r.nu.fartKn} knop`}>{tal(r.nu.nm, 0)} sjömil</Rad>
      )}
      {grupper.length === 0 && r.ekonomisk && (
        <Rad namn={`Med den ekonomiska farten, ${r.ekonomisk.fartKn} knop`}>
          {tal(r.ekonomisk.nm, 0)} sjömil
        </Rad>
      )}
      {grupper.flatMap((g) =>
        g.tankar.map(
          (t) =>
            t.literKvar !== null && (
              <Rad key={t.tank} namn={`${kvar(t.tank)}, ${forstaLiten(t.namn)}`}>
                {tal(t.literKvar, 0)} liter ({tal(t.dieselProcent ?? 0, 0)} %{hur(t.hur)}
                {t.dieselTid !== null ? `, senast kända ${sedan(t.dieselTid, props.nu)}` : ""})
              </Rad>
            ),
        ),
      )}
      {grupper.length > 0 &&
        (["nu", "ekonomisk"] as const).map((typ) => {
          const per = grupper.map((g) => g[typ]);
          const fart = r[typ]?.fartKn ?? per.find((x) => x !== null)?.fartKn;
          if (fart === undefined) return null;
          const baten = r[typ];
          return (
            <Rad
              key={typ}
              namn={
                typ === "nu"
                  ? `Med farten nu, ${fart} knop`
                  : `Med den ekonomiska farten, ${fart} knop`
              }
            >
              {grupper
                .map(
                  (g, i) =>
                    `${i === 0 ? g.namn : forstaLiten(g.namn)} ${g[typ] ? `${tal((g[typ] as { nm: number }).nm, 0)} sjömil` : FINNS_INTE}`,
                )
                .join(", ")}
              . Båten: {baten ? `${tal(baten.nm, 0)} sjömil` : FINNS_INTE}
            </Rad>
          );
        })}
      {(r.nu || r.ekonomisk || grupper.some((g) => g.nu || g.ekonomisk)) && (
        <p class="liten">
          Med 20 % av bränslet kvar som reserv.
          {grupper.length > 0
            ? " Per grupp: gruppens tankar och dess motorers förbrukning. Båten: den kortaste."
            : ""}
        </p>
      )}
      {v && v.nm !== null && (
        <Rad namn="Volvo">
          {tal(v.nm, 0)} sjömil till tom tank
          {v.timmar !== null ? `, ${tal(v.timmar, 1)} timmar` : ""}
          {v.liter !== null ? `, ${tal(v.liter, 0)} liter kvar` : ""} (senast kända{" "}
          {sedan(v.tid, props.nu)})
        </Rad>
      )}
      {r.text && <p class="liten">{r.text}</p>}
    </>
  );
}

/** b12: kortet Resor på Hälsa. */
export function Resekort(props: { f: Fardkort; platsId: string; nu: number }) {
  const f = props.f;
  return (
    <Kort rubrik="Resor">
      {f.senaste.length === 0 ? (
        <p class="meddelande">Inga resor än. En resa börjar när en motor går och båten rör sig.</p>
      ) : (
        <ul class="resor">
          {f.senaste.map((r) => (
            <Resrad key={r.id} r={r} platsId={props.platsId} nu={props.nu} />
          ))}
        </ul>
      )}
      {f.ekonomisk && (
        <Rad namn="Ekonomisk fart (beräknad)">
          {f.ekonomisk.fartKn}–{f.ekonomisk.fartKn + KURVA.stegKnop} knop,{" "}
          {tal(f.ekonomisk.literPerNm, 2)} l/NM
        </Rad>
      )}
      <Rackviddsrader r={f.rackvidd} nu={props.nu} />
      {f.provkorning && (
        <p class="liten">
          Provkörningen "{f.provkorning.etikett}" pågår, sedan {datumOchTid(f.provkorning.start)}.
        </p>
      )}
      <p>
        <a href={`#plats/${props.platsId}/resor`}>Alla resor, förbrukningen och provkörningarna</a>
      </p>
    </Kort>
  );
}

/** Förbrukningskurvan: liter per sjömil per steg, som staplar och som tabell. */
function Kurva(props: { k: Forbrukningskurva }) {
  const p = props.k.punkter;
  if (p.length === 0) {
    return <p class="meddelande">Inga jämna sträckor än för den här tiden.</p>;
  }
  const max = Math.max(...p.map((x) => x.literPerNm));
  const bredd = 320;
  const hojd = 140;
  const steg = bredd / p.length;
  const eko = props.k.ekonomisk?.fartKn;
  // b13: per motor, när någon motor har värden.
  const motorer = MOTORER.filter((m) => p.some((x) => x.motorer?.[m]) && m !== "generator");
  const ekoMotor = props.k.ekonomiskPerMotor;
  return (
    <>
      <svg
        class="kurva"
        viewBox={`0 0 ${bredd} ${hojd + 20}`}
        role="img"
        aria-label="Liter per sjömil mot farten"
      >
        {p.map((x, i) => {
          const h = (x.literPerNm / max) * hojd;
          return (
            <g key={x.fartKn}>
              <rect
                x={i * steg + 2}
                y={hojd - h}
                width={Math.max(2, steg - 4)}
                height={h}
                class={x.fartKn === eko ? "eko" : x.antal < KURVA.minstStrackor ? "fa" : ""}
              />
              <text x={i * steg + steg / 2} y={hojd + 14} text-anchor="middle">
                {x.fartKn}
              </text>
            </g>
          );
        })}
      </svg>
      <table class="tabell">
        <thead>
          <tr>
            <th>Fart (knop)</th>
            <th>l/NM</th>
            {motorer.map((m) => (
              <th key={m}>l/NM {MOTORNAMN[m].toLowerCase()}</th>
            ))}
            <th>l/h</th>
            <th>Sträckor</th>
            <th>Trim</th>
            <th>Diesel</th>
          </tr>
        </thead>
        <tbody>
          {p.map((x) => (
            <tr key={x.fartKn} class={x.fartKn === eko ? "eko" : undefined}>
              <td>
                {x.fartKn}–{x.fartKn + KURVA.stegKnop}
              </td>
              <td>{tal(x.literPerNm, 2)}</td>
              {motorer.map((m) => {
                const y = x.motorer?.[m];
                return <td key={m}>{y ? tal(y.literPerNm, 2) : FINNS_INTE}</td>;
              })}
              <td>{tal(x.literPerTimme, 1)}</td>
              <td>{x.antal}</td>
              <td>{x.trim === null ? FINNS_INTE : `${tal(x.trim, 1)}°`}</td>
              <td>{x.diesel === null ? FINNS_INTE : `${tal(x.diesel, 0)} %`}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p class="liten">
        Bara jämna sträckor: varvtalet inom ±3 % i en minut, över 3 knop och ingen stor kursändring.
        Ett steg behöver minst {KURVA.minstStrackor} sträckor för den ekonomiska farten.
        {props.k.ekonomisk
          ? ` Den ekonomiska farten är beräknad: ${props.k.ekonomisk.fartKn}–${props.k.ekonomisk.fartKn + KURVA.stegKnop} knop.`
          : " Den ekonomiska farten är inte beräknad än."}
        {ekoMotor &&
          MOTORER.some((m) => ekoMotor[m]) &&
          ` Per motor: ${motorerI(ekoMotor)
            .filter((m) => m !== "generator")
            .map((m) => {
              const e = ekoMotor[m];
              return `${MOTORNAMN[m].toLowerCase()} ${e ? `${e.fartKn}–${e.fartKn + KURVA.stegKnop} knop` : FINNS_INTE}`;
            })
            .join(", ")}.`}
      </p>
    </>
  );
}

/** b13: ägaren skriver in en tankning, så att tankens tabell kan använda nivån före och efter. */
function Tankning(props: { v: Fardvisning; platsId: string; satt: (v: Fardvisning) => void }) {
  const tankar = props.v.tankar ?? [];
  const [tank, setTank] = useState(tankar[0]?.instans ?? "diesel");
  const [liter, setLiter] = useState("");
  const [besked, setBesked] = useState<{ ok: boolean; text: string } | null>(null);
  if (!props.v.kanAndra || tankar.length === 0) return null;
  const namn = (t: (typeof tankar)[number]) => forstaLiten(t.namn);
  async function spara(e: Event) {
    e.preventDefault();
    setBesked(null);
    const l = Number(liter.trim().replace(",", "."));
    const t = tankar.find((x) => x.instans === tank);
    try {
      if (!liter.trim() || !Number.isFinite(l))
        throw new Felsvar("Skriv litrarna som ett tal.", 400);
      props.satt(await api.tankning(props.platsId, tank, l));
      setLiter("");
      setBesked({ ok: true, text: `Tankat ${tal(l, 0)} liter i ${t ? namn(t) : tank}.` });
    } catch (x) {
      setBesked({ ok: false, text: x instanceof Felsvar ? x.message : "Det gick inte." });
    }
  }
  return (
    <Kort rubrik="Tankning">
      <form class="formular" onSubmit={spara}>
        <label>
          Tankat (liter)
          <input
            inputMode="decimal"
            value={liter}
            onInput={(e) => setLiter(e.currentTarget.value)}
          />
        </label>
        <label>
          Tanken
          <select value={tank} onChange={(e) => setTank(e.currentTarget.value)}>
            {tankar.map((t) => (
              <option key={t.instans} value={t.instans}>
                {namn(t)}
              </option>
            ))}
          </select>
        </label>
        <div class="knapprad">
          <button class="knapp-huvud" type="submit">
            Spara tankningen
          </button>
        </div>
      </form>
      {besked && (
        <p class={besked.ok ? "ok-text" : "fel"} role={besked.ok ? "status" : "alert"}>
          {besked.text}
        </p>
      )}
      <p class="liten">
        Navet mäter nivån före och efter tankningen, när båten ligger still. Litrarna lär tanken sin
        form, under Inställningar → Motorerna → Tankarna.
      </p>
    </Kort>
  );
}

const sprid = (v: number | null, enhet: string) =>
  v === null ? FINNS_INTE : `${tal(v, 2)} ${enhet}`;

/** Provkörningarna sida vid sida. */
function Jamforelse(props: { p: Provkorning[] }) {
  const rader: [string, (x: Provkorning) => string][] = [
    ["Jämna minuter", (x) => String(x.minuter)],
    ["Spridning i kurs", (x) => sprid(x.kursSpridning, "°")],
    ["Rullning", (x) => sprid(x.rullning, "°")],
    ["Girhastighet", (x) => sprid(x.girSpridning, "°/s")],
    ["Liter per sjömil", (x) => (x.literPerNm === null ? FINNS_INTE : tal(x.literPerNm, 2))],
    ["Fart", (x) => (x.fartKn === null ? FINNS_INTE : `${tal(x.fartKn, 1)} kn`)],
    ["Trim", (x) => sprid(x.trim, "°")],
  ];
  return (
    <table class="tabell jamforelse" aria-label="Provkörningarna sida vid sida">
      <thead>
        <tr>
          <th />
          {props.p.map((x) => (
            <th key={x.id}>{x.etikett}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rader.map(([namn, f]) => (
          <tr key={namn}>
            <th>{namn}</th>
            {props.p.map((x) => (
              <td key={x.id}>{f(x)}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Provkorningar(props: { v: Fardvisning; platsId: string; satt: (v: Fardvisning) => void }) {
  const [etikett, setEtikett] = useState("");
  const [valda, setValda] = useState<string[]>([]);
  const [fel, setFel] = useState<string | null>(null);
  const pagar = props.v.provkorningar.find((p) => p.pagar);
  async function gor(f: () => Promise<Fardvisning>) {
    setFel(null);
    try {
      props.satt(await f());
      setEtikett("");
    } catch (e) {
      setFel(e instanceof Felsvar ? e.message : "Det gick inte.");
    }
  }
  const jamfor = props.v.provkorningar.filter((p) => valda.includes(p.id));
  return (
    <Kort rubrik="Provkörningar" bred>
      <p class="liten">
        Kör samma sträcka i båda riktningarna med varje läge i Humphree och autopiloten, och starta
        en provkörning med läget som etikett. Spridningen räknas ur de jämna sträckorna.
      </p>
      {props.v.kanAndra &&
        (pagar ? (
          <div class="knapprad">
            <button
              class="knapp-huvud"
              type="button"
              onClick={() => gor(() => api.stoppaProvkorning(props.platsId, pagar.id))}
            >
              Stoppa "{pagar.etikett}"
            </button>
          </div>
        ) : (
          <form
            class="formular"
            onSubmit={(e) => {
              e.preventDefault();
              void gor(() => api.startaProvkorning(props.platsId, etikett));
            }}
          >
            <label>
              Etikett för provkörningen
              <input
                value={etikett}
                maxLength={80}
                placeholder="Humphree Auto Trim, autopilot Cruising"
                onInput={(e) => setEtikett(e.currentTarget.value)}
              />
            </label>
            <div class="knapprad">
              <button class="knapp-huvud" type="submit">
                Starta provkörningen
              </button>
            </div>
          </form>
        ))}
      {fel && (
        <p class="fel" role="alert">
          {fel}
        </p>
      )}
      {props.v.provkorningar.length === 0 ? (
        <p class="meddelande">Inga provkörningar än.</p>
      ) : (
        <ul class="provkorningar">
          {props.v.provkorningar.map((p) => (
            <li key={p.id}>
              <label>
                <input
                  type="checkbox"
                  checked={valda.includes(p.id)}
                  onChange={(e) =>
                    setValda((v) =>
                      e.currentTarget.checked ? [...v, p.id] : v.filter((x) => x !== p.id),
                    )
                  }
                />{" "}
                {p.etikett}
              </label>
              <span class="liten">
                {" "}
                {datumOchTid(p.start)}
                {p.pagar ? ", pågår" : p.slut ? `–${datumOchTid(p.slut)}` : ""}, {p.minuter} jämna
                minuter
              </span>
            </li>
          ))}
        </ul>
      )}
      {jamfor.length > 0 && <Jamforelse p={jamfor} />}
      {jamfor.length === 0 && props.v.provkorningar.length > 1 && (
        <p class="liten">Markera två eller fler för att jämföra dem sida vid sida.</p>
      )}
    </Kort>
  );
}

function Servicedel(props: { v: Fardvisning; platsId: string; satt: (v: Fardvisning) => void }) {
  const [timmar, setTimmar] = useState<Record<string, string>>({});
  const [fel, setFel] = useState<string | null>(null);
  async function kvittera(l: Servicelage) {
    setFel(null);
    const text = (timmar[l.motor] ?? "").trim().replace(",", ".");
    try {
      props.satt(await api.kvitteraService(props.platsId, l.motor, text ? Number(text) : null));
      setTimmar((t) => ({ ...t, [l.motor]: "" }));
    } catch (e) {
      setFel(e instanceof Felsvar ? e.message : "Det gick inte.");
    }
  }
  return (
    <Kort rubrik="Servicen">
      {props.v.service.map((l) => (
        <div key={l.motor} class="service">
          <Rad namn={motorrubrik(l.motor)}>{l.text}</Rad>
          {l.senast !== null && (
            <p class="liten">
              Senaste service vid {tal(l.senast, 0)} timmar
              {l.senastTid !== null ? `, ${datumOchTid(l.senastTid)}` : ""}.
            </p>
          )}
          {props.v.kanAndra && l.intervall !== null && (
            <div class="knapprad">
              <label>
                Vid timmar (tomt: timräknaren nu)
                <input
                  inputMode="decimal"
                  value={timmar[l.motor] ?? ""}
                  onInput={(e) => {
                    const v = e.currentTarget.value;
                    setTimmar((t) => ({ ...t, [l.motor]: v }));
                  }}
                />
              </label>
              <button class="knapp-andra" type="button" onClick={() => kvittera(l)}>
                Kvittera servicen för {MOTORNAMN[l.motor].toLowerCase()}
              </button>
            </div>
          )}
        </div>
      ))}
      {fel && (
        <p class="fel" role="alert">
          {fel}
        </p>
      )}
      <p class="liten">Intervallet ställs under Inställningar → Motorerna.</p>
    </Kort>
  );
}

/** Sidan Resor. */
export function Fardvy(props: { platsId: string; nu: number }) {
  const [v, setV] = useState<Fardvisning | null>(null);
  const [fel, setFel] = useState<string | null>(null);
  const [period, setPeriod] = useState<"sasong" | "manad">("sasong");
  const hamta = useCallback(() => {
    api
      .farder(props.platsId)
      .then((x) => {
        setV(x);
        setFel(null);
      })
      .catch(() => setFel("Resorna gick inte att hämta."));
  }, [props.platsId]);
  useEffect(() => {
    hamta();
    const t = setInterval(hamta, 30_000);
    return () => clearInterval(t);
  }, [hamta]);
  if (fel && !v) return <p class="tomt">{fel}</p>;
  if (!v) return <p class="laddar">Hämtar …</p>;
  const k = period === "sasong" ? v.sasong : v.manad;
  return (
    <div class="rutnat farder">
      <h2 class="kort-bred">Resor och förbrukning</h2>
      <Kort rubrik="Resorna" bred>
        {v.resor.length === 0 ? (
          <p class="meddelande">
            Inga resor än. En resa börjar när en motor går och båten rör sig över 2 knop i en minut,
            och slutar när båda motorerna har stått i 10 minuter. Bara sträckan, tiden och
            förbrukningen sparas, inte spåret.
          </p>
        ) : (
          <ul class="resor">
            {v.resor.map((r) => (
              <Resrad key={r.id} r={r} platsId={props.platsId} nu={props.nu} />
            ))}
          </ul>
        )}
      </Kort>
      <Kort rubrik="Förbrukningskurvan" bred>
        <div class="knapprad">
          <button
            type="button"
            class={period === "sasong" ? "knapp-huvud" : "knapp-andra"}
            aria-pressed={period === "sasong"}
            onClick={() => setPeriod("sasong")}
          >
            Säsongen
          </button>
          <button
            type="button"
            class={period === "manad" ? "knapp-huvud" : "knapp-andra"}
            aria-pressed={period === "manad"}
            onClick={() => setPeriod("manad")}
          >
            30 dagar
          </button>
        </div>
        <Kurva k={k} />
      </Kort>
      <Kort rubrik="Räckvidden">
        <Rackviddsrader r={v.rackvidd} nu={props.nu} />
      </Kort>
      <Tankning v={v} platsId={props.platsId} satt={setV} />
      <Servicedel v={v} platsId={props.platsId} satt={setV} />
      <Provkorningar v={v} platsId={props.platsId} satt={setV} />
    </div>
  );
}

function perMotor(v: PerMotor<number>, enhet: string, d = 1): string {
  return motorerI(v)
    .map((m) => `${MOTORNAMN[m]} ${tal2(v[m], enhet, d)}`)
    .join(", ");
}

/** Sidan för en resa. */
export function Fardsidevy(props: { platsId: string; fardId: string; nu: number }) {
  const [s, setS] = useState<Fardsida | null>(null);
  const [fel, setFel] = useState<string | null>(null);
  useEffect(() => {
    setS(null);
    api
      .fard(props.platsId, props.fardId)
      .then(setS)
      .catch(() => setFel("Resan finns inte."));
  }, [props.platsId, props.fardId]);
  if (fel) return <p class="tomt">{fel}</p>;
  if (!s) return <p class="laddar">Hämtar …</p>;
  const r = s.resa;
  return (
    <div class="rutnat farder">
      <p class="kort-bred">
        <a href={`#plats/${props.platsId}/resor`}>← Alla resor</a>
      </p>
      <Kort rubrik={`Resan ${datumOchTid(r.start)}${r.pagar ? " (pågår)" : ""}`} bred>
        <Rad namn="Tiden">
          {langd(r.sekunder)}
          {r.slut !== null ? `, till ${datumOchTid(r.slut)}` : ""}
        </Rad>
        <Rad namn="Sträckan">{tal(r.distansNm, 2)} sjömil</Rad>
        <Rad namn="Förbrukningen">
          {r.literTotalt === null ? FINNS_INTE : `${tal(r.literTotalt, 1)} liter`}
        </Rad>
        <Rad namn="Per motor">{perMotor(r.liter, "l")}</Rad>
        {r.volvoLiter && (
          <Rad namn="Volvo">{perMotor(r.volvoLiter, "l")}, enligt motorernas tripp</Rad>
        )}
        <Rad namn="Liter per sjömil">
          {r.literPerNm === null ? FINNS_INTE : tal(r.literPerNm, 2)}
        </Rad>
        <Rad namn="Liter per timme">
          {r.literPerTimme === null ? FINNS_INTE : tal(r.literPerTimme, 1)}
        </Rad>
        <Rad namn="Medelfart">{tal2(r.medelfartKn, "kn")}</Rad>
        <Rad namn="Högsta fart">{tal2(r.maxfartKn, "kn")}</Rad>
        <Rad namn="Motortimmar">{perMotor(r.motortimmar, "h", 2)}</Rad>
        {(r.tankar ?? []).length === 0 ? (
          <Rad namn="Dieseltanken">
            {r.diesel === null
              ? FINNS_INTE
              : `${tal(r.diesel.fran, 0)} % → ${tal(r.diesel.till, 0)} %${r.diesel.liter !== null ? `, ${tal(r.diesel.liter, 0)} liter` : ""}`}
          </Rad>
        ) : (
          (r.tankar ?? []).map((t) => (
            <Rad
              key={t.tank}
              namn={`${t.tank.startsWith("bensin") ? "Bensinen" : "Dieseln"}, ${forstaLiten(t.namn)}`}
            >
              {tal(t.fran, 0)} % → {tal(t.till, 0)} %
              {t.liter !== null ? `, ${tal(t.liter, 0)} liter` : ""}
            </Rad>
          ))
        )}
        <p class="liten">
          Sträckan är farten över grund (GPS), och förbrukningen motorernas bränsleflöde. Spåret
          sparas inte.
        </p>
      </Kort>
      {s.info.length > 0 && (
        <Kort rubrik="Under resan" bred>
          <ul>
            {s.info.map((i) => (
              <li key={`${i.tid}${i.text}`}>
                {datumOchTid(i.tid)}: {i.text}
              </li>
            ))}
          </ul>
        </Kort>
      )}
      <Kort rubrik="Minuterna" bred>
        <table class="tabell">
          <thead>
            <tr>
              <th>Tid</th>
              <th>Fart</th>
              <th>l/NM</th>
              <th>Varvtal</th>
              <th>Trim</th>
              <th>Autopilot</th>
              <th>Jämn</th>
            </tr>
          </thead>
          <tbody>
            {s.minuter.map((m) => (
              <tr key={m.start}>
                <td>{datumOchTid(m.start)}</td>
                <td>{m.sogKn === null ? FINNS_INTE : tal(m.sogKn, 1)}</td>
                <td>{m.literPerNm === null ? FINNS_INTE : tal(m.literPerNm, 2)}</td>
                <td>
                  {motorerI(m.varv)
                    .map((x) => ((m.varv[x] ?? null) === null ? "–" : tal(m.varv[x] as number, 0)))
                    .join(" / ")}
                </td>
                <td>{m.trim === null ? FINNS_INTE : `${tal(m.trim, 1)}°`}</td>
                <td>{m.autopilot ? (AUTOPILOT[m.autopilot] ?? m.autopilot) : FINNS_INTE}</td>
                <td>{m.jamn ? "ja" : `nej: ${m.orsak}`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Kort>
    </div>
  );
}
